/**
 * FloatingEdge — edge that connects at the inner shape border of each node,
 * inset from the outer Handle ring by HANDLE_PAD.
 *
 * Used for constraint (structural) connections so edges touch inner node
 * perimeters naturally and arrows are visible.
 */

import React, { useState, CSSProperties } from 'react';
import {
  getBezierPath,
  useInternalNode,
  Position,
  EdgeLabelRenderer,
  type EdgeProps,
  type Edge,
} from '@xyflow/react';
import { useScope } from '../../contexts/ScopeContext';
import { useRuntime } from '../../contexts/RuntimeProvider';

// Must match HANDLE_PAD in StructuralNode
const HANDLE_PAD = 7;

// ─── Geometry helpers ────────────────────────────────────────

/** Determine which Position best describes the angle from one point to another. */
function positionFromAngle(cx: number, cy: number, px: number, py: number): Position {
  const dx = px - cx;
  const dy = py - cy;
  if (Math.abs(dx) > Math.abs(dy)) {
    return dx > 0 ? Position.Right : Position.Left;
  }
  return dy > 0 ? Position.Bottom : Position.Top;
}

/**
 * Compute intersection of the line from targetCenter → nodeCenter
 * with the node's inner shape (measured bounds inset by HANDLE_PAD).
 */
function getInnerIntersection(node: any, targetNode: any) {
  const pos = node.internals?.positionAbsolute ?? node.position;
  const tPos = targetNode.internals?.positionAbsolute ?? targetNode.position;

  // Inner shape bounds (inset by HANDLE_PAD)
  const iw = (node.measured?.width ?? 0) - HANDLE_PAD * 2;
  const ih = (node.measured?.height ?? 0) - HANDLE_PAD * 2;
  const w = Math.max(iw, 1) / 2;
  const h = Math.max(ih, 1) / 2;

  // Centers of the inner shapes
  const cx = pos.x + (node.measured?.width ?? 0) / 2;
  const cy = pos.y + (node.measured?.height ?? 0) / 2;
  const tx = tPos.x + (targetNode.measured?.width ?? 0) / 2;
  const ty = tPos.y + (targetNode.measured?.height ?? 0) / 2;

  const xx1 = (tx - cx) / (2 * w) - (ty - cy) / (2 * h);
  const yy1 = (tx - cx) / (2 * w) + (ty - cy) / (2 * h);
  const a = 1 / (Math.abs(xx1) + Math.abs(yy1) || 1);
  const xx3 = a * xx1;
  const yy3 = a * yy1;

  return {
    x: w * (xx3 + yy3) + cx,
    y: h * (-xx3 + yy3) + cy,
  };
}

function getEdgeParams(source: any, target: any) {
  const si = getInnerIntersection(source, target);
  const ti = getInnerIntersection(target, source);

  const sc = {
    x: (source.internals?.positionAbsolute ?? source.position).x + (source.measured?.width ?? 0) / 2,
    y: (source.internals?.positionAbsolute ?? source.position).y + (source.measured?.height ?? 0) / 2,
  };
  const tc = {
    x: (target.internals?.positionAbsolute ?? target.position).x + (target.measured?.width ?? 0) / 2,
    y: (target.internals?.positionAbsolute ?? target.position).y + (target.measured?.height ?? 0) / 2,
  };

  return {
    sx: si.x, sy: si.y,
    tx: ti.x, ty: ti.y,
    sourcePos: positionFromAngle(sc.x, sc.y, tc.x, tc.y),
    targetPos: positionFromAngle(tc.x, tc.y, sc.x, sc.y),
  };
}

// ─── Edge component ──────────────────────────────────────────

interface FloatingEdgeData extends Record<string, unknown> {
  disabled?: boolean;
}

type FloatingEdgeType = Edge<FloatingEdgeData>;

export function FloatingEdge(props: EdgeProps<FloatingEdgeType>) {
  const { source, target, markerEnd } = props;
  const id = props.id as string;
  const style = (props.style ?? {}) as CSSProperties;
  const data = props.data as FloatingEdgeData | undefined;
  const [isHovered, setIsHovered] = useState(false);
  const { instanceId } = useScope();
  const { toggleConnectionDisabled, removeConnection } = useRuntime();

  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);

  if (!sourceNode || !targetNode) return null;

  const { sx, sy, tx, ty, sourcePos, targetPos } = getEdgeParams(sourceNode, targetNode);
  const disabled = data?.disabled ?? false;

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX: sx, sourceY: sy, sourcePosition: sourcePos,
    targetX: tx, targetY: ty, targetPosition: targetPos,
  });

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    const [src, tgt] = id.split('->');
    if (src && tgt) removeConnection(instanceId, src, tgt);
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleConnectionDisabled(instanceId, id);
  };

  const showButton = disabled || isHovered;

  return (
    <>
      {/* Wide invisible path for hover */}
      <path
        d={edgePath}
        fill="none"
        strokeWidth={40}
        stroke="transparent"
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />
      {/* Visible edge */}
      <path
        id={id}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          fill: 'none',
          strokeWidth: 2,
          ...(disabled ? { opacity: 0.3, strokeDasharray: '5,5' } : {}),
        }}
      />
      {/* Controls */}
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
            opacity: showButton ? 1 : 0,
            transition: 'opacity 0.15s ease',
            display: 'flex',
            gap: 4,
          }}
          className="nodrag nopan"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <button
            onClick={handleToggle}
            style={{
              width: 28, height: 28, borderRadius: '50%', border: 'none',
              background: disabled ? '#ef4444' : '#374151', color: 'white',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
            }}
            title={disabled ? 'Enable connection' : 'Disable connection'}
          >
            {disabled ? '⊘' : '●'}
          </button>
          <button
            onClick={handleDelete}
            style={{
              width: 28, height: 28, borderRadius: '50%', border: 'none',
              background: '#374151', color: '#ef4444',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
            }}
            title="Delete connection"
          >
            ✕
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

// ─── Connection line (while dragging) ────────────────────────

/**
 * Custom connection line that computes bezier handle directions from the
 * source node center → mouse position, so the curve flows naturally
 * regardless of which side of the node you drag from.
 */
export function FloatingConnectionLine({
  fromNode, fromX, fromY, toX, toY, connectionLineStyle,
}: any) {
  if (!fromNode) return null;

  const w = fromNode.measured?.width ?? 0;
  const h = fromNode.measured?.height ?? 0;
  const pos = fromNode.internals?.positionAbsolute ?? fromNode.position;
  const cx = pos.x + w / 2;
  const cy = pos.y + h / 2;

  const sourcePos = positionFromAngle(cx, cy, toX, toY);
  const targetPos = positionFromAngle(toX, toY, cx, cy);

  const [path] = getBezierPath({
    sourceX: fromX,
    sourceY: fromY,
    sourcePosition: sourcePos,
    targetX: toX,
    targetY: toY,
    targetPosition: targetPos,
  });

  return (
    <g>
      <path d={path} fill="none" strokeWidth={2} style={connectionLineStyle} />
    </g>
  );
}
